/**
 * Permission catalogue: `module.resource.action` (PLAN.md §10). One source of truth for the server
 * (enforcement), the web role matrix and the future mobile app.
 */
export type Action =
  | 'view'
  | 'create'
  | 'edit'
  | 'delete'
  | 'approve'
  | 'send'
  | 'export'
  | 'import'
  | 'post'
  | 'close'
  | 'reopen';

const CRUD: Action[] = ['view', 'create', 'edit', 'delete', 'export', 'import'];
const DOC: Action[] = ['view', 'create', 'edit', 'delete', 'approve', 'send', 'export', 'import'];

/** module -> resource -> allowed actions */
export const PERMISSION_CATALOGUE: Record<string, Record<string, Action[]>> = {
  dashboard: { overview: ['view'] },
  accounting: {
    report: ['view', 'export'],
    ledger: ['view', 'export'],
    journal: ['view', 'create', 'post', 'delete'],
    account: ['view', 'create', 'edit'],
    period: ['view', 'close', 'reopen'],
  },
  purchases: {
    order: DOC,
    payout: DOC,
    vendor: CRUD,
    bill: DOC,
    expense: DOC,
  },
  sales: {
    invoice: DOC,
    receipt: DOC,
    proforma: DOC,
    quotation: DOC,
    creditnote: DOC,
    client: CRUD,
    item: CRUD,
    recurring: CRUD,
  },
  crm: { lead: CRUD, pipeline: ['view', 'create', 'edit', 'delete'] },
  team: {
    user: ['view', 'create', 'edit', 'delete'],
    role: ['view', 'create', 'edit', 'delete'],
  },
  banking: {
    account: ['view', 'create', 'edit'],
    transfer: ['view', 'create'],
    reconciliation: ['view', 'create', 'edit'],
  },
  settings: {
    company: ['view', 'edit'],
    tax: ['view', 'edit'],
    numbering: ['view', 'edit'],
    template: ['view', 'edit'],
    reminder: ['view', 'edit'],
    audit: ['view', 'export'],
    communication: ['view', 'send'],
  },
};

export const ALL_PERMISSION_KEYS: string[] = Object.entries(PERMISSION_CATALOGUE).flatMap(
  ([module, resources]) =>
    Object.entries(resources).flatMap(([resource, actions]) =>
      actions.map((a) => `${module}.${resource}.${a}`),
    ),
);

const KEY_SET = new Set(ALL_PERMISSION_KEYS);
export const isKnownPermission = (key: string) => key === '*' || KEY_SET.has(key);

/** Expands `module.*`, `module.resource.*` and `*.*.view` style patterns into concrete keys. */
export function expandPermissions(patterns: readonly string[]): string[] {
  const out = new Set<string>();
  for (const pattern of patterns) {
    if (pattern === '*') {
      out.add('*');
      continue;
    }
    const [m = '*', r = '*', a = '*'] = pattern.split('.');
    for (const key of ALL_PERMISSION_KEYS) {
      const [km, kr, ka] = key.split('.');
      if ((m === '*' || m === km) && (r === '*' || r === kr) && (a === '*' || a === ka)) {
        out.add(key);
      }
    }
  }
  return [...out];
}

export type DataScope = 'own' | 'all';

export interface DefaultRole {
  name: string;
  description: string;
  requiresTwoFactor: boolean;
  scope: DataScope;
  /** Approval limit in base currency; null = unlimited. */
  approvalLimit: number | null;
  permissions: string[];
}

const salesDocs = ['sales.quotation', 'sales.proforma', 'sales.invoice'];

export const DEFAULT_ROLES: DefaultRole[] = [
  {
    name: 'Owner',
    description: 'Everything, including users, roles, settings and period reopen.',
    requiresTwoFactor: true,
    scope: 'all',
    approvalLimit: null,
    permissions: ['*'],
  },
  {
    name: 'Admin',
    description: 'Everything except Owner-only actions.',
    requiresTwoFactor: true,
    scope: 'all',
    approvalLimit: null,
    permissions: ['*.*.*'],
  },
  {
    name: 'Accountant',
    description: 'Full accounting, banking, purchases and sales documents; no user management.',
    requiresTwoFactor: true,
    scope: 'all',
    approvalLimit: null,
    permissions: [
      'dashboard.*',
      'accounting.report.*',
      'accounting.ledger.*',
      'accounting.journal.*',
      'accounting.account.*',
      'accounting.period.view',
      'accounting.period.close',
      'banking.*',
      'purchases.*',
      'sales.*',
      'crm.lead.view',
      'settings.tax.view',
      'settings.numbering.view',
      'settings.company.view',
      'settings.communication.*',
    ],
  },
  {
    name: 'Sales',
    description: 'CRM, clients, quotes, proformas and invoices (own data by default).',
    requiresTwoFactor: false,
    scope: 'own',
    approvalLimit: 5_000_000,
    permissions: [
      'dashboard.overview.view',
      'crm.lead.*',
      'crm.pipeline.view',
      'sales.client.*',
      'sales.item.view',
      'sales.receipt.view',
      'sales.recurring.view',
      'sales.recurring.create',
      'sales.recurring.edit',
      ...salesDocs.flatMap((r) =>
        ['view', 'create', 'edit', 'approve', 'send', 'export'].map((a) => `${r}.${a}`),
      ),
    ],
  },
  {
    name: 'Procurement',
    description: 'Vendors, purchase orders, bills and expenses; no approval above limit.',
    requiresTwoFactor: false,
    scope: 'all',
    approvalLimit: 1_000_000,
    permissions: [
      'dashboard.overview.view',
      'purchases.vendor.*',
      'purchases.order.view',
      'purchases.order.create',
      'purchases.order.edit',
      'purchases.order.send',
      'purchases.order.approve',
      'purchases.bill.view',
      'purchases.bill.create',
      'purchases.bill.edit',
      'purchases.bill.approve',
      'purchases.expense.view',
      'purchases.expense.create',
      'purchases.expense.edit',
      'purchases.expense.approve',
      'sales.item.view',
    ],
  },
  {
    name: 'Auditor',
    description: 'Read-only on everything, with export.',
    requiresTwoFactor: false,
    scope: 'all',
    approvalLimit: null,
    permissions: ['*.*.view', '*.*.export'],
  },
];
